import { Constants } from '@pwc/pwc-deals-web-common';
import { MiniLoader } from '@pwc/pwc-deals-ui';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import PropType from 'prop-types';
import React, { useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';

import { EmptyClipboardModal } from 'common/components/EmptyClipboardModal';
import { POWER_PIVOT_LIBRARY_ITEM_TYPES } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes';
import UnableToLoadContent from 'features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import CONSTANTS from 'features/analyses/Freeform/FreeformAnalysisLayout/AddReportDataLayoutContainer/Constants';
import {
  clearClipboard,
  getLibraryItemsFromClipboard,
  postRichText,
} from 'features/contentLibrary/libraryItemService';

import { showModalError } from '../../../../../redux/errors/errorsSlice';
import { UNABLE_TO_ADD_OBJECT } from '../../../../ModalError/constants';
import ObjectInjestPopover from '../../../../SectionArea/ObjectInjestPopover';
import { useDragAndDrop, useSections } from '../../../SectionContext';
import { setCurrentSections } from '../../../SectionContextUtils';
import DeleteButton from '../DeleteButton/DeleteButton';
import DraggableLibraryItemCard from './DraggableLibraryItemCard';
import { getStyle } from './utils';
// TODO: CRA Upgrade Tech Debt
import { PowerBiContainer } from '../../../../SelectReport/PowerBiContainer';
import { TableauContainer } from '../../../../SelectReport/TableauContainer';

import styles from './SectionArea.module.scss';
import {
  HIDE_LOST_COMMENTS_DIALOG_SESSION_STORAGE,
  LostCommentsWarningDialog,
} from '../../../../LostCommentsWarningDialog';

function SectionArea({
  areaClass,
  droppableLocation,
  libraryItems,
  libraryItemMap,
  droppableLocations,
  sectionData,
  showRightPanel,
  objectInjestPopoverAddButtonTitle,
  isPowerBiEnabled = true,
  loading = false,
  panelNumber = 0,
}) {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const [popOver, onPopover] = useState(false);
  const [showPowerBIModal, setPowerBIModalDisplay] = useState(false);
  const [noDataOnClipboard, setNoDataOnClipboard] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);
  const [clearSectionHover, setClearSectionHover] = useState(false);
  const [showTableauModal, setShowTableauModal] = useState(false);
  const [
    // eslint-disable-next-line no-unused-vars
    { currentSections = [], nonLaidOutLibraryItems = [] },
    // eslint-disable-next-line no-unused-vars
    contextDispatch,
  ] = useSections();
  const { setDroppableLocations } = useDragAndDrop();
  const [showLostCommentsModal, setShowLostCommentsModal] = useState(false);

  const isLostCommentsModalHidden =
    window.sessionStorage.getItem(HIDE_LOST_COMMENTS_DIALOG_SESSION_STORAGE) ===
    'true';

  const onAddRichText = async (droppableLocation2) => {
    onPopover(false);

    try {
      const name = uuidv4();
      const richTextData = JSON.stringify({
        RichTextData: ' ',
      });

      const id = await postRichText(projectId, name, richTextData);
      if (!id) {
        dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
      } else {
        const newSections = cloneDeep(currentSections);

        newSections[sectionData.order - 1].items.push({
          id,
          name,
          type: Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value,
          isShared: false,
          data: richTextData,
          analysisColumn: droppableLocation2.position,
        });

        setCurrentSections(newSections, contextDispatch);

        const newDroppableLocations = cloneDeep(droppableLocations);
        newDroppableLocations[droppableLocation.id] = {
          ...droppableLocation,
          libraryItemIds: [...droppableLocation.libraryItemIds, id],
        };
        setDroppableLocations(newDroppableLocations);
      }
    } catch (e) {
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
    }
  };

  const onAddPBIItem = async (PBIItem, droppableLocation2) => {
    onPopover(false);
    setPowerBIModalDisplay(false);

    const newSections = cloneDeep(currentSections);

    newSections[sectionData.order - 1].items.push({
      ...PBIItem,
      analysisColumn: droppableLocation2.position,
    });

    setCurrentSections(newSections, contextDispatch);

    const newDroppableLocations = cloneDeep(droppableLocations);
    newDroppableLocations[droppableLocation.id] = {
      ...droppableLocation,
      libraryItemIds: [...droppableLocation.libraryItemIds, PBIItem.id],
    };
    setDroppableLocations(newDroppableLocations);
  };

  const onAddTableauItem = async (tableauItem, droppableLocation2) => {
    onPopover(false);
    setShowTableauModal(false);

    const newSections = cloneDeep(currentSections);

    newSections[sectionData.order - 1].items.push({
      ...tableauItem,
      analysisColumn: droppableLocation2.position,
    });

    setCurrentSections(newSections, contextDispatch);

    const newDroppableLocations = cloneDeep(droppableLocations);
    newDroppableLocations[droppableLocation.id] = {
      ...droppableLocation,
      libraryItemIds: [...droppableLocation.libraryItemIds, tableauItem.id],
    };
    setDroppableLocations(newDroppableLocations);
  };

  const onAddExcelOrPPT = async (droppableLocation2) => {
    try {
      const libraryItemResponse = await getLibraryItemsFromClipboard(projectId);

      if (!libraryItemResponse) {
        setNoDataOnClipboard(true);
        return;
      }

      const { data, id, name, status, type } = libraryItemResponse;

      let libraryItemData = {
        GroupedByKey: data.driveId,
        DriveId: data.driveId,
        DriveItemId: data.driveItemId,
        SharePointItemUrl: data.sharePointItemUrl,
        SiteId: data.siteId,
      };

      switch (type) {
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value:
          libraryItemData = {
            ...libraryItemData,
            WorksheetName: data.worksheetName,
            UniqueId: data.uniqueId,
            ChartName: data.chartName,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value:
          libraryItemData = {
            ...libraryItemData,
            FormatType: data.formatType,
            Name: data.name,
            UniqueId: data.uniqueId,
            WorksheetName: data.worksheetName,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value:
          libraryItemData = {
            ...libraryItemData,
            WorksheetName: data.worksheetName,
            UniqueId: data.uniqueId,
            Name: data.name,
            NamedRange: data?.namedRange,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value:
          libraryItemData = {
            ...libraryItemData,
            SlideNumber: data.slideNumber,
            GroupName: data.groupName,
          };
          break;
        case POWER_PIVOT_LIBRARY_ITEM_TYPES.DynamicExcelCellRange:
        case POWER_PIVOT_LIBRARY_ITEM_TYPES.DynamicExcelChart:
        case POWER_PIVOT_LIBRARY_ITEM_TYPES.DynamicExcelNamedRange:
          libraryItemData = {
            ...libraryItemData,
            ...data,
          };
          break;
        default:
          break;
      }

      const libraryItemCreated = {
        data: JSON.stringify(libraryItemData),
        formatType: data.formatType,
        id,
        name,
        status,
        type,
      };

      const newSections = cloneDeep(currentSections);

      newSections[sectionData.order - 1].items.push({
        ...libraryItemCreated,
        analysisColumn: droppableLocation2.position,
      });

      setCurrentSections(newSections, contextDispatch);

      const newDroppableLocations = cloneDeep(droppableLocations);
      newDroppableLocations[droppableLocation.id] = {
        ...droppableLocation,
        libraryItemIds: [
          ...droppableLocation.libraryItemIds,
          libraryItemCreated.id,
        ],
      };
      setDroppableLocations(newDroppableLocations);

      // clear the clipboard
      await clearClipboard(projectId, libraryItemResponse);
    } catch (e) {
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
    }
  };

  const hideClearSectionButton = () => {
    setMouseOver(false);
  };

  const clearSection = () => {
    const newSections = cloneDeep(currentSections);

    newSections[sectionData.order - 1].items = newSections[
      sectionData.order - 1
    ].items.filter((x) => x.analysisColumn !== droppableLocation.position);
    setCurrentSections(newSections, contextDispatch);

    const newDroppableLocations = cloneDeep(droppableLocations);
    newDroppableLocations[droppableLocation.id] = {
      ...droppableLocation,
      libraryItemIds: [],
    };
    setDroppableLocations(newDroppableLocations);
  };

  return (
    <Droppable
      droppableId={droppableLocation.id}
      direction={droppableLocation.direction}
      key={droppableLocation.id}
      ignoreContainerClipping
    >
      {(provided, snapshot) => {
        const computedStyle = getStyle(
          snapshot,
          droppableLocation.id,
          popOver,
          libraryItemMap,
          libraryItems
        );
        return (
          <div
            className={`${areaClass} ${styles.sectionAreaDroppableContainer}`}
            data-testid={areaClass}
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={computedStyle}
            onMouseEnter={() => setMouseOver(true)}
            onFocus={() => setMouseOver(true)}
            onMouseLeave={hideClearSectionButton}
            onBlur={hideClearSectionButton}
          >
            <div
              className={classNames(styles.sectionAreaItemContainer, {
                'background-left':
                  droppableLocation.id.includes('text-overflow'),
              })}
            >
              {libraryItems.map((libraryItem, index) => {
                if (noDataOnClipboard || libraryItem.type < 0) {
                  const err = libraryItem.error || {};
                  const data = libraryItem.data || {};

                  return (
                    <UnableToLoadContent
                      apiErrorCode={err.apiErrorCode}
                      data={{
                        sharePointItemUrl: data.sharePointItemUrl,
                      }}
                      errorCode={err.errorCode}
                      key={libraryItem.id}
                      projectId={projectId}
                      type={err.type}
                    />
                  );
                }

                return (
                  <DraggableLibraryItemCard
                    key={libraryItem.id}
                    libraryItem={libraryItem}
                    index={index}
                    droppableLocationId={droppableLocation.id}
                    droppableLocations={droppableLocations}
                    panelNumber={panelNumber}
                  />
                );
              })}
              {provided.placeholder}
            </div>

            <div
              className={styles.clearSectionWrapper}
              style={{
                display:
                  mouseOver &&
                  libraryItems.length > 0 &&
                  !snapshot.isDraggingOver &&
                  !showRightPanel
                    ? 'flex'
                    : 'none',
              }}
            >
              <DeleteButton
                tooltip="Clear section"
                tooltipPlacement="top"
                iconType={`${
                  clearSectionHover ? 'fill' : 'outline'
                } icon-grey-theme`}
                handleClick={() => {
                  if (isLostCommentsModalHidden) {
                    clearSection();
                  } else {
                    setShowLostCommentsModal(true);
                  }
                }}
                handleMouseEnter={() => setClearSectionHover(true)}
                handleMouseLeave={() => setClearSectionHover(false)}
              />
              <LostCommentsWarningDialog
                open={showLostCommentsModal}
                onConfirm={() => {
                  setShowLostCommentsModal(false);
                  clearSection();
                }}
                onClose={() => {
                  setShowLostCommentsModal(false);
                }}
              />
            </div>

            {libraryItems.length < 1 && (
              <div
                className={classNames(styles.sectionAreaBackground, {
                  'background-left':
                    droppableLocation.id.includes('text-overflow'),
                })}
              >
                <div className={styles.sectionAreaBackgroundContent}>
                  {loading ? (
                    <MiniLoader />
                  ) : (
                    <>
                      <ObjectInjestPopover
                        onInsertText={() => onAddRichText(droppableLocation)}
                        onInsertPowerBi={() => {
                          // TODO: jm - use common modal for powerbi and tableau
                          setPowerBIModalDisplay(true);
                        }}
                        onInsertTableau={() => {
                          // TODO: jm - use common modal for powerbi and tableau
                          setShowTableauModal(true);
                        }}
                        onInsertExcelOrPPT={() =>
                          onAddExcelOrPPT(droppableLocation)
                        }
                        onPopover={onPopover}
                        droppableLocation={droppableLocation}
                        allowTextOnly={
                          CONSTANTS.LAYOUT_TYPE_TO_SECTION_CLASSNAME_MAP[
                            sectionData.layoutType
                          ] === 'one-column-text-overflow-area'
                        }
                        buttonTitle={objectInjestPopoverAddButtonTitle}
                        isPowerBiEnabled={isPowerBiEnabled}
                      />
                      {showPowerBIModal && (
                        <PowerBiContainer
                          show={showPowerBIModal}
                          onAdd={(PBIItem) =>
                            onAddPBIItem(PBIItem, droppableLocation)
                          }
                          onHide={() => setPowerBIModalDisplay(false)}
                        />
                      )}
                      {showTableauModal && (
                        <TableauContainer
                          show={showTableauModal}
                          onAdd={(tableauItem) => {
                            onAddTableauItem(tableauItem, droppableLocation);
                          }}
                          onHide={() => setShowTableauModal(false)}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {droppableLocation.id.includes('text-overflow') && (
              <div
                className={`${styles.sectionAreaBackground} background-right`}
              />
            )}
            {noDataOnClipboard && (
              <EmptyClipboardModal
                show={noDataOnClipboard}
                onHide={() => setNoDataOnClipboard(false)}
              />
            )}
          </div>
        );
      }}
    </Droppable>
  );
}

SectionArea.propTypes = {
  areaClass: PropType.string,
};

SectionArea.defaultProps = {
  areaClass: '',
};

export default SectionArea;
